import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { request, useOperations } from './OperationsProvider';

// Saying that a lorry runs captive traffic, or taking that back.
//
// A captive lorry shuttles between the company's own sites all day and never comes to a gate desk.
// Two of the plant's rules assume every lorry does, and on a fleet like this they are true for ever:
// one fires whenever a lorry is inside the plant with no open trip, which is its whole day, and the
// other every time one leaves without a Gate Out, which is every run. Left alone they refill the
// exception list with a fleet that is working perfectly.
//
// The dialog says what stops and what does not, because a mark that quietly switches rules off is
// worth being nervous about: it is only the two rules that look for paperwork. Speed, dwell, a
// device going quiet, a lorry read where it should not be - all of those still stand against it, and
// so does every count and every report.
export default function MarkCaptive({ vehicle, onClose }) {
  const { refresh } = useOperations();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const captive = vehicle.fleet === 'CAPTIVE';

  const apply = async () => {
    setPending(true); setError('');
    try {
      await request(`/api/operations/vehicles/${vehicle.id}/fleet`, 'PATCH', { fleet: captive ? '' : 'CAPTIVE' });
      await refresh();
      onClose();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  return (
    <Dialog open onClose={() => !pending && onClose()} fullWidth maxWidth="xs">
      <DialogTitle>{captive ? `${vehicle.name} back to gate traffic?` : `${vehicle.name} runs captive traffic?`}</DialogTitle>
      <DialogContent>
        <Stack gap={1.5}>
          <Typography variant="body2" color="text.secondary">
            {captive
              ? `${vehicle.name} will be expected to carry gate paperwork again: it will be reported for standing inside the plant with no open trip, and for leaving without a Gate Out.`
              : `${vehicle.name} runs between your own sites and never comes to a gate desk, so it will not be asked for a challan, a Gate In or a Gate Out — and the complaints already standing against it for not having them are cleared.`}
          </Typography>
          {!captive && (
            <Typography variant="body2" color="text.secondary">
              Everything that is not about paperwork still applies: speed, dwell, a device going
              quiet, a reader somewhere it should not be. Its trips and its delays are counted
              exactly as any other truck&apos;s are.
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={pending} onClick={apply}>
          {pending ? 'Working…' : captive ? 'Back to gate traffic' : 'Mark captive'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
